import React, { useMemo, useState } from 'react';
import { addDays, format, subWeeks } from 'date-fns';
import { toast } from 'sonner';
import { aiService, type AutoFillSuggestion } from '@/services/aiService';
import type { Project, Timesheet, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

const EMPTY_HOURS = {
  monday: 0,
  tuesday: 0,
  wednesday: 0,
  thursday: 0,
  friday: 0,
  saturday: 0,
  sunday: 0,
};

export interface UseTimesheetBulkEntryToolsParams {
  setEntries: React.Dispatch<React.SetStateAction<TimesheetEntry[]>>;
  entries: TimesheetEntry[];
  timesheets: Timesheet[];
  targetEmployeeId: number;
  currentWeekStart: Date;
  allAvailableProjects: Project[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  isDaily: boolean;
}

export interface UseTimesheetBulkEntryToolsResult {
  // Copy last week
  showCopyLastWeekDialog: boolean;
  setShowCopyLastWeekDialog: React.Dispatch<React.SetStateAction<boolean>>;
  copyLastWeekSourceEntries: TimesheetEntry[];
  copyFromLastWeek: () => void;
  handleCopyLastWeekConfirm: (selectedEntries: TimesheetEntry[]) => void;
  // Smart auto-fill
  showAutoFillDialog: boolean;
  setShowAutoFillDialog: React.Dispatch<React.SetStateAction<boolean>>;
  autoFillSuggestions: ReturnType<typeof aiService.generateAutoFillSuggestions>;
  handleApplyAutoFill: (suggestions: AutoFillSuggestion[]) => void;
  // Bulk description fill
  showFillDescriptionDialog: boolean;
  setShowFillDescriptionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  bulkDescription: string;
  setBulkDescription: React.Dispatch<React.SetStateAction<string>>;
  handleFillEmptyDescriptions: () => void;
}

/**
 * Bulk row-population tools: copy last week, AI smart auto-fill and filling
 * every empty description with the same text.
 */
export function useTimesheetBulkEntryTools({
  setEntries,
  entries,
  timesheets,
  targetEmployeeId,
  currentWeekStart,
  allAvailableProjects,
  organizationSettings,
  isDaily,
}: UseTimesheetBulkEntryToolsParams): UseTimesheetBulkEntryToolsResult {
  const [showCopyLastWeekDialog, setShowCopyLastWeekDialog] = useState(false);
  const [copyLastWeekSourceEntries, setCopyLastWeekSourceEntries] = useState<TimesheetEntry[]>([]);
  const [showAutoFillDialog, setShowAutoFillDialog] = useState(false);
  const [showFillDescriptionDialog, setShowFillDescriptionDialog] = useState(false);
  const [bulkDescription, setBulkDescription] = useState('');

  // AI-powered auto-fill suggestions
  const autoFillSuggestions = useMemo(() => {
    return aiService.generateAutoFillSuggestions(
      targetEmployeeId,
      timesheets,
      currentWeekStart,
      allAvailableProjects
    );
  }, [targetEmployeeId, timesheets, currentWeekStart, allAvailableProjects]);

  const copyFromLastWeek = () => {
    const lastWeek = subWeeks(currentWeekStart, 1);
    const lastWeekEnding = addDays(lastWeek, 6);
    const lastTimesheet = timesheets.find(
      ts => ts.employeeId === targetEmployeeId &&
      format(ts.weekEnding, 'yyyy-MM-dd') === format(lastWeekEnding, 'yyyy-MM-dd')
    );

    if (!lastTimesheet || lastTimesheet.entries.length === 0) {
      toast.error('No timesheet found for last week');
      return;
    }

    const timesheetNativeEntries = lastTimesheet.entries.filter(entry => {
      if (entry.isFromSchedule) return false;
      if (entry.sourceIntegration) return false;
      if (entry.absenceType) return false;
      return true;
    });

    if (timesheetNativeEntries.length === 0) {
      toast.error('No timesheet entries found to copy (absences and scheduled bookings are excluded)');
      return;
    }

    setCopyLastWeekSourceEntries(timesheetNativeEntries);
    setShowCopyLastWeekDialog(true);
  };

  const handleCopyLastWeekConfirm = (selectedEntries: TimesheetEntry[]) => {
    if (selectedEntries.length === 0) return;

    const shouldCopyDescriptions = organizationSettings?.deadline?.copyDescriptions ?? true;

    setEntries(selectedEntries.map((entry, index) => ({
      ...entry,
      id: -Date.now() - index,
      description: shouldCopyDescriptions ? entry.description : '',
      dailyNotes: isDaily ? (shouldCopyDescriptions ? { ...entry.dailyNotes } : {}) : entry.dailyNotes,
      hours: { ...EMPTY_HOURS },
    })));
  };

  const handleApplyAutoFill = (suggestions: AutoFillSuggestion[]) => {
    const newEntries = suggestions.map((suggestion, index) => ({
      id: -Date.now() - index,
      projectId: suggestion.projectId,
      costCentreId: suggestion.costCentreId,
      description: isDaily ? '' : suggestion.description,
      dailyNotes: isDaily ? (suggestion.dailyNotes ? { ...suggestion.dailyNotes } : {}) : suggestion.dailyNotes,
      hours: suggestion.hours as TimesheetEntry['hours'],
    }));
    setEntries(prevEntries => [...prevEntries, ...newEntries]);
  };

  const handleFillEmptyDescriptions = () => {
    if (!bulkDescription.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const updatedEntries = entries.map(entry => {
      if (!entry.description.trim()) {
        return { ...entry, description: bulkDescription.trim() };
      }
      return entry;
    });

    setEntries(updatedEntries);
    setShowFillDescriptionDialog(false);
    setBulkDescription('');
  };

  return {
    showCopyLastWeekDialog,
    setShowCopyLastWeekDialog,
    copyLastWeekSourceEntries,
    copyFromLastWeek,
    handleCopyLastWeekConfirm,
    showAutoFillDialog,
    setShowAutoFillDialog,
    autoFillSuggestions,
    handleApplyAutoFill,
    showFillDescriptionDialog,
    setShowFillDescriptionDialog,
    bulkDescription,
    setBulkDescription,
    handleFillEmptyDescriptions,
  };
}
